import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { ToolStatus } from '@new_project/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface EnvironmentStepProps {
  onContinue: () => void;
}

function StatusRow({ name, status }: { name: string; status: ToolStatus }) {
  return (
    <li className="flex items-center justify-between border-gray-100 border-b py-2">
      <span className="font-medium">{name}</span>
      <span className={status.installed ? 'text-success' : 'text-danger'}>
        {status.installed ? `✓ ${status.version ?? 'ok'}` : '✗ not found'}
      </span>
    </li>
  );
}

/**
 * Generator preflight: shows whether copier/python/git/pnpm are available and
 * offers a one-click Copier install (into a project-local venv) when missing.
 */
export function EnvironmentStep({ onContinue }: EnvironmentStepProps) {
  const queryClient = useQueryClient();
  const envQuery = useQuery({ queryKey: ['environment'], queryFn: api.getEnvironment });

  const install = useMutation({
    mutationFn: api.installCopier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['environment'] }),
  });

  const env = envQuery.data;

  return (
    <section className="space-y-4">
      <h1 className="font-bold text-2xl text-bleu-nuit">Environment check</h1>
      <p className="text-sm opacity-70">
        The generator runs <code>copier</code> locally for Copier templates. Templates without a{' '}
        <code>copier.yml</code> are cloned as-is and need only <code>git</code>, so you can continue
        without Copier — you’ll just be limited to non-Copier templates.
      </p>

      {envQuery.isPending ? <p>Checking…</p> : null}
      {envQuery.isError ? (
        <p className="text-danger">Could not reach the backend. Is it running on :3001?</p>
      ) : null}

      {env ? (
        <>
          <ul className="rounded border border-gray-200 px-3">
            <StatusRow name="Copier" status={env.copier} />
            <StatusRow name="Python" status={env.python} />
            <StatusRow name="git" status={env.git} />
            <StatusRow name="pnpm" status={env.pnpm} />
          </ul>

          {!env.copier.installed ? (
            <div className="space-y-2 rounded border border-jaune-deep/30 bg-jaune-tint p-3">
              <p className="text-sm">
                Copier isn’t installed. Install it into a local venv (<code>{env.venvDir}</code>)?
                {!env.python.installed ? ' Requires Python 3, which was not found.' : null}
              </p>
              <Button
                type="button"
                disabled={install.isPending || !env.python.installed}
                onClick={() => install.mutate()}
              >
                {install.isPending ? 'Installing… (this can take a minute)' : 'Install Copier'}
              </Button>
              {install.isError ? (
                <p className="text-danger text-sm">
                  {install.error instanceof Error ? install.error.message : 'Install failed.'}
                </p>
              ) : null}
            </div>
          ) : null}

          <Button type="button" disabled={!env.git.installed} onClick={onContinue}>
            {env.copier.installed ? 'Continue' : 'Continue without Copier'}
          </Button>
        </>
      ) : null}
    </section>
  );
}
